import React from 'react';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';
import { formatCurrency, getCurrencySymbol } from '@src/scripts/util/currency';

const ProductStep = ( {
	products,
	selectedProductId,
	pricePaid,
	onSelectProduct,
	onChangePrice,
	currency,
	locale,
} ) => {
	// A Labor calculator instance only ever offers one solution (matching
	// the reference tool, which has no picker at all in that mode) — skip
	// the grid and just show it as a fixed, non-interactive info block.
	const showPicker = products.length > 1;
	const singleProduct = ! showPicker ? products[ 0 ] : null;

	return (
		<div className="roi-calculator__step roi-calculator__step--product">
			<h3>
				{ showPicker
					? __( 'Select your device', 'sprintray-roi-calculator' )
					: __(
							'Your SprintRay solution',
							'sprintray-roi-calculator'
					  ) }
			</h3>

			{ showPicker && (
				<div className="roi-calculator__product-grid">
					{ products.map( ( product ) => (
						<button
							type="button"
							key={ product.id }
							className={
								'roi-calculator__product-card' +
								( product.id === selectedProductId
									? ' is-selected'
									: '' )
							}
							onClick={ () => onSelectProduct( product ) }
						>
							{ product.image && (
								<img
									src={ product.image }
									alt={ product.name }
								/>
							) }
							<span className="roi-calculator__product-name">
								{ product.name }
							</span>
							{ product.subtitle && (
								<span className="roi-calculator__product-subtitle">
									{ product.subtitle }
								</span>
							) }
							<span className="roi-calculator__product-price">
								{ formatCurrency(
									product.listPrice,
									currency,
									locale
								) }
							</span>
						</button>
					) ) }
				</div>
			) }

			{ singleProduct && (
				<div className="roi-calculator__product-card roi-calculator__product-card--fixed">
					<span className="roi-calculator__product-name">
						{ singleProduct.name }
					</span>
					{ singleProduct.subtitle && (
						<span className="roi-calculator__product-subtitle">
							{ singleProduct.subtitle }
						</span>
					) }
				</div>
			) }

			{ selectedProductId && (
				<label
					className="roi-calculator__field roi-calculator__field--price"
					htmlFor="roi-calculator-price-paid"
				>
					{ __( 'Price actually paid', 'sprintray-roi-calculator' ) }
					<span className="roi-calculator__input-suffix">
						<input
							id="roi-calculator-price-paid"
							type="number"
							step="1"
							min="0"
							// Clearing the field used to store 0 (Number('')
							// === 0), which redisplayed as "0" instead of
							// blank — typing a new digit then landed after
							// that "0" instead of replacing it. Reporting
							// `null` on empty (rather than 0) and rendering
							// it as '' lets the field go genuinely blank.
							value={ pricePaid ?? '' }
							onChange={ ( event ) => {
								const raw = event.target.value;
								onChangePrice(
									raw === '' ? null : Number( raw )
								);
							} }
						/>
						<span aria-hidden="true">
							{ getCurrencySymbol( currency, locale ) }
						</span>
					</span>
				</label>
			) }
		</div>
	);
};

ProductStep.propTypes = {
	products: PropTypes.array.isRequired,
	selectedProductId: PropTypes.oneOfType( [
		PropTypes.number,
		PropTypes.string,
	] ),
	pricePaid: PropTypes.number,
	onSelectProduct: PropTypes.func.isRequired,
	onChangePrice: PropTypes.func.isRequired,
	currency: PropTypes.string,
	locale: PropTypes.string,
};

export default ProductStep;
